














































import { defineComponent, toRefs, computed, PropType } from '@vue/composition-api';
import {
  AdgroupFormData,
  CampaignFormData,
  AdgroupConfig,
  OptimizeGoal,
  fakeOptimizeGoal,
  AdgroupListItem,
  CreateMode,
  ExternalAction,
} from '@tt4b/ttam-enums';
import { StateEnum, useStore, useAdgroupMode, useStdAdgroupIs } from '@tt4b/ttam-hooks';
import TtamPromotionPixelCompose from '@/components/adgroup/promotion-pixel/promotion-pixel-compose.vue';
import OptimizationGoal from './index';
import AttributionTips from '@/components/adgroup/attribution-tips/index.vue';

const name = 'ttam-optimization-goal-shop-compose';
export default defineComponent({
  name,
  components: {
    TtamOptimizationGoalDefaultSelect: OptimizationGoal.TtamOptimizationGoalDefaultSelect,
    TtamOptimizationGoalExplanationHint: OptimizationGoal.TtamOptimizationGoalExplanationHint,
    TtamOptimizationGoalLabel: OptimizationGoal.TtamOptimizationGoalLabel,
    TtamPromotionPixelCompose,
    TtamAttributionTips: AttributionTips,
  },
  props: {
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      required: true,
      default: () => ({}),
    },
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      required: true,
      default: () => ({}),
    },
    dcAdgroupList: {
      type: Array as PropType<AdgroupListItem[]>,
      required: true,
      default: () => [],
    },
    createMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.CREATE,
    },
    adgroupConfig: {
      type: Object as PropType<AdgroupConfig>,
      required: true,
      default: () => ({}),
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const { adgroupData, campaignData, adgroupConfig, dcAdgroupList, createMode } = toRefs(props);
    const { isLiveAds, isStandardAdsWithShop, isStandardAdsWithShowcase, isPSA } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });
    const { isEdit } = useAdgroupMode({
      createMode,
    });

    /** Get Data From Store  */
    const { state } = useStore();
    const voStatus = computed(() => state[StateEnum.voStatus].data.value);

    /** DefaultSelect  */
    const { options: optimizationGoalOptions } = OptimizationGoal.useDefaultSelectPropsOptions({
      adgroupData,
      campaignData,
      voStatus,
    });

    const _adRefPixelId = computed({
      get: () => adgroupData.value?.adRefPixelId,
      set: val => {
        emit('pixel-change', val);
      },
    });

    const _externalAction = computed({
      get: () => adgroupData.value?.externalAction,
      set: val => {
        emit('pixel-event-change', val);
      },
    });

    const _optimizationGoal = computed({
      get: () => {
        if (isLiveAds.value || isStandardAdsWithShowcase.value || isPSA.value) {
          return (
            optimizationGoalOptions.value?.find(
              o =>
                o.realValue === adgroupData.value.optimizeGoal && o.externalAction === adgroupData.value.externalAction,
            )?.value ?? ''
          );
        } else if (isStandardAdsWithShop.value) {
          if (
            ![OptimizeGoal.CLICK, OptimizeGoal.CONVERT, OptimizeGoal.VALUE].includes(adgroupData.value?.optimizeGoal)
          ) {
            return '';
          }

          if (adgroupData.value.optimizeGoal === OptimizeGoal.CONVERT) {
            if (
              !adgroupData.value.adRefPixelId &&
              [ExternalAction.shopping, ExternalAction.initiateOrder].includes(adgroupData.value.externalAction)
            ) {
              return optimizationGoalOptions.value?.find(
                o => o.realValue === OptimizeGoal.CONVERT && o.externalAction === adgroupData.value.externalAction,
              )?.value;
            } else {
              return fakeOptimizeGoal.OFFSITE_EVENT;
            }
          }

          return adgroupData.value.optimizeGoal;
        }

        return '';
      },
      set: val => {
        _adRefPixelId.value = '';
        const target = optimizationGoalOptions.value?.find(o => o.value === val);
        if (isLiveAds.value || isStandardAdsWithShowcase.value || isPSA.value) {
          _externalAction.value = optimizationGoalOptions.value?.find(o => o.value === val)?.externalAction;
          emit('optimize-goal-change', target.realValue);
        } else if (isStandardAdsWithShop.value) {
          if (target) {
            _externalAction.value = target.externalAction;
            emit('optimize-goal-change', target.realValue);
          }
        }
      },
    });

    const { disabled: defaultSelectDisabled } = OptimizationGoal.useDefaultSelectPropsDisabled({
      adgroupData,
      campaignData,
      dcAdgroupList,
      adgroupConfig,
    });

    const noSelection = computed(() => !optimizationGoalOptions.value?.length);
    const selectedOptGoalLabel = computed(() => {
      const item = optimizationGoalOptions.value?.find?.(v => v.value === _optimizationGoal.value);

      return item?.label || '';
    });

    /** ExplanationTips  */
    const { tips: explanationTips } = OptimizationGoal.useExplanationTipsPropsTips({
      adgroupData,
    });

    /** offline event pixel  */

    const isPixelVisible = computed(() => _optimizationGoal.value === fakeOptimizeGoal.OFFSITE_EVENT);

    const onPixelChange = (val: string) => {
      _adRefPixelId.value = val;
      _externalAction.value = 0;
    };
    const onPixelEventChange = (val: number) => {
      _externalAction.value = val;
    };

    return {
      name,

      trackProps: computed(() => {
        if (isLiveAds.value) {
          return {};
        }

        return {
          'data-tea-shop_type': adgroupData.value?.shopType,
        };
      }),

      isEdit,

      _optimizationGoal,
      _adRefPixelId,
      _externalAction,
      optimizationGoalOptions,
      defaultSelectDisabled,
      noSelection,
      selectedOptGoalLabel,

      explanationTips,

      isPixelVisible,
      onPixelChange,
      onPixelEventChange,
    };
  },
});
